import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import type {Editor} from "@tiptap/core";
import {DEFAULT_PRINTOUT_PARAGRAPH_VARIANT} from "../../blocks";
import {AlignmentIcon, BlockFormatIcon, ListFormatIcon} from "./toolbarIcons";
import {FormatMenuButton, ToolbarIconButton, ToolbarPopover} from "./toolbarControls";
import {
    BLOCK_FORMATS,
    getAlignmentLabels,
    getBlockFormatLabels,
    getListFormatLabels,
    HEADING_LEVEL_BY_BLOCK_FORMAT,
    isInList,
    isParagraphBlockFormat,
    LIST_FORMATS,
    TEXT_ALIGNMENTS,
    toggleListFormat,
    type BlockFormat,
    type ListFormat,
    type TextAlignment,
} from "./printoutToolbarFormats";
import type {ToolbarState} from "./printoutToolbarState";

interface Props {
    editor: Editor | null;
    state: ToolbarState;
}

const ToolbarBlockMenus = ({editor, state}: Props) => {

    const {t} = useTranslation();
    const [alignmentMenuAnchorEl, setAlignmentMenuAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [blockFormatMenuAnchorEl, setBlockFormatMenuAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [listFormatMenuAnchorEl, setListFormatMenuAnchorEl] = useState<HTMLButtonElement | null>(null);

    const runCommand = (command: (editor: Editor) => void) => {
        if (!editor) return;
        command(editor);
    };

    const alignmentLabels = getAlignmentLabels(t);
    const blockFormatLabels = getBlockFormatLabels(t);
    const listFormatLabels = getListFormatLabels(t);

    const canTextAlign: Record<TextAlignment, boolean> = {
        left: state.canAlignLeft,
        center: state.canAlignCenter,
        right: state.canAlignRight,
        justify: state.canAlignJustify,
    };

    const canBlockFormat = state.canBlockFormat;

    const canListFormat: Record<ListFormat, boolean> = {
        bullet: state.canBulletList,
        ordered: state.canOrderedList,
    };

    const alignmentMenuOpen = Boolean(alignmentMenuAnchorEl);
    const alignmentMenuDisabled = !TEXT_ALIGNMENTS.some((alignment) => canTextAlign[alignment]);
    const blockFormatMenuOpen = Boolean(blockFormatMenuAnchorEl);
    const blockFormatMenuDisabled = !BLOCK_FORMATS.some((format) => canBlockFormat[format]);
    const listFormatMenuOpen = Boolean(listFormatMenuAnchorEl);
    const listFormatMenuDisabled = !LIST_FORMATS.some((format) => canListFormat[format]);
    const activeListFormat: ListFormat = state.isOrderedList ? 'ordered' : 'bullet';
    const listFormatMenuLabel = state.isBulletList || state.isOrderedList
        ? listFormatLabels[activeListFormat]
        : t('Lists');

    const isCurrentBlockFormat = (format: BlockFormat) => state.blockFormat === format;
    const isCurrentListFormat = (format: ListFormat) => (
        (format === 'bullet' && state.isBulletList)
        || (format === 'ordered' && state.isOrderedList)
    );
    const isCurrentAlignment = (alignment: TextAlignment) => state.textAlign === alignment;

    const setTextAlignment = (alignment: TextAlignment) => {
        runCommand((editor) => editor.chain().focus().setTextAlign(alignment).run());
        setAlignmentMenuAnchorEl(null);
    };

    const setBlockFormat = (format: BlockFormat) => {
        const headingLevel = HEADING_LEVEL_BY_BLOCK_FORMAT[format];
        const paragraphVariant = isParagraphBlockFormat(format) ? format : DEFAULT_PRINTOUT_PARAGRAPH_VARIANT;

        runCommand((editor) => {
            const chain = editor.chain().focus();

            if (isInList(editor)) {
                chain.liftListItem('listItem');
            }

            if (headingLevel) {
                chain.setHeading({level: headingLevel}).run();
                return;
            }

            chain.setNode('paragraph', {variant: paragraphVariant}).run();
        });
        setBlockFormatMenuAnchorEl(null);
    };

    const setListFormat = (format: ListFormat) => {
        runCommand((editor) => toggleListFormat(editor, format));
        setListFormatMenuAnchorEl(null);
    };

    return (
        <>
            <FormatMenuButton
                disabled={blockFormatMenuDisabled}
                icon={<BlockFormatIcon format={state.blockFormat}/>}
                iconOnly
                label={blockFormatLabels[state.blockFormat]}
                onClick={(event) => setBlockFormatMenuAnchorEl(event.currentTarget)}
                showChevron
            />
            <FormatMenuButton
                disabled={listFormatMenuDisabled}
                icon={<ListFormatIcon format={activeListFormat}/>}
                iconOnly
                label={listFormatMenuLabel}
                onClick={(event) => setListFormatMenuAnchorEl(event.currentTarget)}
                showChevron
            />
            <ToolbarIconButton
                active={alignmentMenuOpen || state.textAlign !== 'left'}
                disabled={alignmentMenuDisabled}
                label={alignmentLabels[state.textAlign]}
                onClick={(event) => setAlignmentMenuAnchorEl(event.currentTarget)}
            >
                <AlignmentIcon alignment={state.textAlign}/>
            </ToolbarIconButton>

            <ToolbarPopover
                anchorEl={blockFormatMenuAnchorEl}
                onClose={() => setBlockFormatMenuAnchorEl(null)}
                open={blockFormatMenuOpen}
            >
                {BLOCK_FORMATS.map((format) => (
                    <FormatMenuButton
                        active={state.blockFormat === format}
                        disabled={!canBlockFormat[format] && !isCurrentBlockFormat(format)}
                        icon={<BlockFormatIcon format={format}/>}
                        key={format}
                        label={blockFormatLabels[format]}
                        onClick={() => setBlockFormat(format)}
                    />
                ))}
            </ToolbarPopover>
            <ToolbarPopover
                anchorEl={listFormatMenuAnchorEl}
                onClose={() => setListFormatMenuAnchorEl(null)}
                open={listFormatMenuOpen}
            >
                {LIST_FORMATS.map((format) => (
                    <FormatMenuButton
                        active={isCurrentListFormat(format)}
                        disabled={!canListFormat[format] && !isCurrentListFormat(format)}
                        icon={<ListFormatIcon format={format}/>}
                        key={format}
                        label={listFormatLabels[format]}
                        onClick={() => setListFormat(format)}
                    />
                ))}
            </ToolbarPopover>
            <ToolbarPopover
                anchorEl={alignmentMenuAnchorEl}
                direction="row"
                onClose={() => setAlignmentMenuAnchorEl(null)}
                open={alignmentMenuOpen}
            >
                {TEXT_ALIGNMENTS.map((alignment) => (
                    <ToolbarIconButton
                        active={isCurrentAlignment(alignment)}
                        disabled={!canTextAlign[alignment] && !isCurrentAlignment(alignment)}
                        key={alignment}
                        label={alignmentLabels[alignment]}
                        onClick={() => setTextAlignment(alignment)}
                    >
                        <AlignmentIcon alignment={alignment}/>
                    </ToolbarIconButton>
                ))}
            </ToolbarPopover>
        </>
    );
};

export default ToolbarBlockMenus;
